import { useState } from 'react'

import { Button as FactorialOneButton } from '@factorialco/factorial-one-react'
import { Check } from '@factorialco/factorial-one-react/icons/app'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import { ProductUpdatesPoliciesDocument } from 'generated/resources/graphql'

import { useModuleAvailability } from 'modules/core/hooks/useModuleAvailability'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { useCheckModuleAcquired } from 'modules/marketplace_modules/hooks/useCheckModuleAcquired'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

import LegacyPlanHintModal from '../LegacyPlanHintModal'

type Props = {
  chargebeeModuleName: string
  productUpdateSlug: string
  featureIdentifier?: string
  label?: string
  version?: 'factorial-one' | 'factorial'
  source?: string
  moreInfo?: string
  disableIcon?: boolean
  pageSource?: string
}

const UpsellButtons = ({
  chargebeeModuleName,
  productUpdateSlug,
  featureIdentifier,
  pageSource,
  version,
  source,
  label,
}: Props) => {
  const [isLegacyModalOpen, setIsLegacyModalOpen] = useState(false)
  const isModuleAcquired: boolean = useCheckModuleAcquired(chargebeeModuleName)
  const isFeatureRelatedAcquired = useModuleAvailability(featureIdentifier || '').moduleAvailability
    .available

  const { data: upsellData } = useQuery(ProductUpdatesPoliciesDocument)
  const canSeeUpsellButton = upsellData?.productUpdates._policies.upsellButtonSeeUpsellButton.can
  const canSeeModules = upsellData?.modules._policies.moduleManageModule.can

  if (isModuleAcquired) {
    return version === 'factorial-one' ? (
      <FactorialOneButton
        variant="outline"
        label={i18n.t('product_updates.in_your_plan')}
        icon={Check}
        disabled
      />
    ) : null
  }

  if (
    !canSeeUpsellButton ||
    (isModuleAcquired && !!featureIdentifier && isFeatureRelatedAcquired)
  ) {
    return null
  }

  const buttonLabel = canSeeModules
    ? label || i18n.t('product_updates.get_this_feature')
    : i18n.t('product_updates.upgrade_legacy_plan.cta')

  const content = () => {
    if (isModuleAcquired && !!featureIdentifier && !isFeatureRelatedAcquired) {
      return {
        // eslint-disable-next-line no-type-assertion/no-type-assertion
        moduleRequested: chargebeeModuleName as AvailableModules,
        requestReason: ValidReasons.MissingFeature,
        requestSource: source
          ? `${source}_${productUpdateSlug}_feature`
          : `news_${productUpdateSlug}_feature`,
        // eslint-disable-next-line no-type-assertion/no-type-assertion
        featureRequested: featureIdentifier as Features,
        pageSource: pageSource || '',
        label: buttonLabel,
      } as const
    }

    return {
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      moduleRequested: chargebeeModuleName as AvailableModules,
      requestReason: ValidReasons.MissingAddon,
      requestSource: source ? `${source}_${productUpdateSlug}` : `news_${productUpdateSlug}`,
      pageSource: pageSource || '',
      label: buttonLabel,
      showModuleModal: true,
    } as const
  }

  return (
    <>
      <UpsellingKit.UpsellButton {...content()} />
      {isLegacyModalOpen && <LegacyPlanHintModal onClose={() => setIsLegacyModalOpen(false)} />}
    </>
  )
}

export default UpsellButtons
